import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useAxesTooltip } from "@mui/x-charts";
import type { FC } from "react";
import { Trans } from "react-i18next";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	findInterpolatedPeriod,
	type InterpolatedPeriod,
} from "web_myplann/Portfolio/Helpers/interpolatedPeriods";

const width = 180;
const noDataWidth = 220;
const padding = 16;

type Props = {
	pos: { x: number; y: number };
	interpolatedPeriods?: InterpolatedPeriod[];
};

export const DataLineChartTooltip: FC<Props> = ({
	pos,
	interpolatedPeriods,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const tooltip = useAxesTooltip();

	if (!tooltip?.[0]) return null;

	const data = tooltip[0];
	const period = findInterpolatedPeriod(
		interpolatedPeriods ?? [],
		data.axisValue,
	);
	const boxWidth = period ? noDataWidth : width;
	const top = pos.y - 100;
	let left = pos.x - boxWidth / 2;

	if (left < padding) {
		left = padding;
	}

	if (left > window.innerWidth - (boxWidth + padding)) {
		left = window.innerWidth - (boxWidth + padding);
	}

	if (period) {
		return (
			<Stack
				position={"absolute"}
				left={left}
				top={top}
				padding={"8px 10px"}
				borderRadius={0.4}
				width={boxWidth}
				bgcolor={theme.palette.deprecated.grey[50]}
			>
				<Typography
					fontSize={12}
					lineHeight={1.4}
					color={theme.palette.deprecated.grey[700]}
				>
					<Trans
						i18nKey={getResourcePath(Resources.Portfolio.Chart.NoDataPeriod)}
						values={{ from: period.fromLabel, to: period.toLabel }}
						components={{
							b: <Box component="span" fontWeight={700} />,
						}}
					/>
				</Typography>
			</Stack>
		);
	}

	return (
		<Stack
			position={"absolute"}
			left={left}
			top={top}
			padding={"4px 8px"}
			borderRadius={0.4}
			width={boxWidth}
			bgcolor={theme.palette.deprecated.grey[50]}
		>
			<Stack direction={"row"} justifyContent={"space-between"}>
				<Typography
					variant="deprecatedCaption"
					color={theme.palette.deprecated.grey[500]}
				>
					{t(Resources.Portfolio.Chart.Date)}
				</Typography>
				<Typography
					fontSize={12}
					fontWeight={500}
					color={theme.palette.deprecated.grey[700]}
				>
					{data.axisFormattedValue}
				</Typography>
			</Stack>
			{data.seriesItems
				.filter((item) => {
					const rawValue = item.value;
					return rawValue !== null && rawValue !== undefined && rawValue !== 0;
				})
				.map(({ formattedLabel, formattedValue }) => (
					<Stack
						key={formattedLabel}
						direction={"row"}
						justifyContent={"space-between"}
					>
						<Typography
							variant="deprecatedCaption"
							color={theme.palette.deprecated.grey[500]}
						>
							{formattedLabel}
						</Typography>
						<Typography
							fontSize={12}
							fontWeight={500}
							color={theme.palette.deprecated.grey[700]}
						>
							{formattedValue}
						</Typography>
					</Stack>
				))}
		</Stack>
	);
};
